import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import ClientHeader from "../components/ClientHeader";
import NavSidebar from "../components/NavSidebar";
import { useNotification } from "../contexts/NotificationContext";
import { scrapeCampsitePrices } from "../services/anthropicService";
import { getUserDetails } from "../api";
import SubscribeUpsellModal from "../components/SubscribeUpsellModal";

function PriceComparisonPage() {
  const navigate = useNavigate();
  const { showInfo } = useNotification();
  const username = localStorage.getItem("username") || "User";
  
  const [url, setUrl] = useState("");
  const [checkinDate, setCheckinDate] = useState("");
  const [checkoutDate, setCheckoutDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");

  const [subs, setSubs] = useState({ is_chat_subscribed: false, is_voice_subscribed: false });
  const [subscribeOpen, setSubscribeOpen] = useState(false);
  const [subscribeProduct, setSubscribeProduct] = useState("chat");

  // AI features are disabled by default so no provider key ships to the browser.
  const aiEnabled = process.env.REACT_APP_AI_FEATURES_ENABLED === 'true';

  const handleOpenSubscribe = (which) => {
    setSubscribeProduct(which || "chat");
    setSubscribeOpen(true);
  };

  const handleStartSubscription = async ({ product }) => {
    const clientIdRaw = localStorage.getItem("client_id");
    const clientId = clientIdRaw ? Number(clientIdRaw) : null;
    showInfo(
      'Request Received', 
      'Thank you for your interest. A member of our team will contact you shortly.',
      0 // Don't auto-dismiss - requires manual close
    );
    try {
      const resp = await getUserDetails(clientId);
      const data = resp?.data ?? resp ?? {};
      const next = {
        is_chat_subscribed: !!data.is_chat_subscribed,
        is_voice_subscribed: !!data.is_voice_subscribed,
      };
      setSubs(next);
    } catch (e) {
      console.error(e);
    } finally {
      setSubscribeOpen(false);
    }
  };

  useEffect(() => {
    let on = true;
    (async () => {
      try {
        const clientIdRaw = localStorage.getItem("client_id");
        const clientId = clientIdRaw ? Number(clientIdRaw) : null;
        if (!clientId) throw new Error("Missing client id.");
        const resp = await getUserDetails(clientId);
        const data = resp?.data ?? resp ?? {};
        const next = {
          is_chat_subscribed: !!data.is_chat_subscribed,
          is_voice_subscribed: !!data.is_voice_subscribed,
        };
        if (!on) return;
        setSubs(next);
      } catch {
        if (!on) return;
        setSubs({ is_chat_subscribed: false, is_voice_subscribed: false });
      }
    })();
    return () => { on = false; };
  }, []);

  const handleAnalyze = async () => {
    if (!aiEnabled) {
      setError("This feature is temporarily under maintenance.");
      return;
    }
    if (!url || !checkinDate || !checkoutDate) {
      setError("Please fill in all fields");
      return;
    }

    setLoading(true);
    setError("");
    setResults(null);

    try {
      const response = await scrapeCampsitePrices(url, checkinDate, checkoutDate);
      
      if (response.success) {
        setResults(response);
      } else {
        setError(response.error || "Failed to analyze prices");
      }
    } catch (e) {
      setError(e.message || "Failed to connect to AI service");
    } finally {
      setLoading(false);
    }
  };

  const getTotalNights = () => {
    if (!checkinDate || !checkoutDate) return 0;
    const start = new Date(checkinDate);
    const end = new Date(checkoutDate);
    const nights = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    return nights > 0 ? nights : 0;
  };

  const calculateTotalPrice = (pricePerNight) => {
    const nights = getTotalNights();
    const price = parseFloat(pricePerNight.replace(/[^0-9.]/g, ''));
    return isNaN(price) ? "N/A" : `$${(price * nights).toFixed(2)}`;
  };

  return (
    <div className="bg-white dark:bg-gray-950 h-screen flex flex-col overflow-hidden transition-colors duration-200">
      <ClientHeader username={username} initials={username.slice(0, 2).toUpperCase()} activePage="Dashboard" />

      <div className="flex flex-1 min-h-0">
        <NavSidebar activePage={null} />

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto bg-[#f7f8fa] dark:bg-gray-950">
          <div className="max-w-6xl mx-auto px-6 py-8">
            {/* Header */}
            <div className="mb-6">
              <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                AI Price Comparison
                <span className="text-sm px-2.5 py-1 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full font-semibold">
                  Powered by Claude
                </span>
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-1">
                Use AI to analyze and compare campsite pricing information
              </p>
            </div>

        {/* Input Form */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <svg className="w-5 h-5 text-[#32B1FF]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            Search Criteria
          </h2>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                CampSpot URL <span className="text-red-500">*</span>
              </label>
              <input
                type="url"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://campspot.com/..."
                className="w-full border dark:border-gray-700 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#32B1FF] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Check-in Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={checkinDate}
                  onChange={(e) => setCheckinDate(e.target.value)}
                  className="w-full border dark:border-gray-700 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#32B1FF] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Check-out Date <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={checkoutDate}
                  onChange={(e) => setCheckoutDate(e.target.value)}
                  min={checkinDate}
                  className="w-full border dark:border-gray-700 rounded-lg px-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[#32B1FF] bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
              </div>
            </div>

            {getTotalNights() > 0 && (
              <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>{getTotalNights()} night{getTotalNights() !== 1 ? 's' : ''}</strong> selected
                </p>
              </div>
            )}

            <button
              onClick={handleAnalyze}
              disabled={loading || !aiEnabled}
              title={aiEnabled ? undefined : "This feature is temporarily under maintenance"}
              className="w-full md:w-auto px-6 py-3 bg-[#32B1FF] hover:bg-[#259ae6] text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition shadow-sm"
            >
              {loading ? (
                <span className="flex items-center gap-2 justify-center">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Analyzing with AI... (10-15 seconds)
                </span>
              ) : (
                <span className="flex items-center gap-2 justify-center">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                  Analyze Prices with AI
                </span>
              )}
            </button>
          </div>

          {error && (
            <div className="mt-4 p-4 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-start gap-3">
              <svg className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd"/>
              </svg>
              <p className="text-sm text-red-700 dark:text-red-400 flex-1">{error}</p>
            </div>
          )}
        </div>

        {/* Results Display */}
        {results && results.success && (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <svg className="w-6 h-6 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"/>
                </svg>
                Found {results.total_sites_found} Sites
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    // Build URL with dates as query parameters
                    const urlObj = new URL(url);
                    urlObj.searchParams.set('arrival', checkinDate);
                    urlObj.searchParams.set('departure', checkoutDate);
                    window.open(urlObj.toString(), '_blank', 'noopener,noreferrer');
                  }}
                  className="px-4 py-2 rounded-lg border-2 border-[#32B1FF] text-[#32B1FF] hover:bg-[#32B1FF] hover:text-white transition font-semibold text-sm flex items-center gap-2"
                  title={`Open with dates: ${checkinDate} to ${checkoutDate}`}
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                  </svg>
                  Visit Site with Dates
                </button>
                <span className="px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-semibold">
                  Analysis Complete
                </span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {results.sites?.map((site, idx) => (
                <div
                  key={idx}
                  className={`p-5 rounded-xl border-2 transition hover:shadow-lg ${
                    site.available
                      ? "border-green-300 dark:border-green-700 bg-green-50 dark:bg-green-900/20"
                      : "border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 opacity-75"
                  }`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-semibold text-gray-900 dark:text-gray-100 flex-1 text-sm">
                      {site.type}
                    </h3>
                    <span
                      className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                        site.available
                          ? "bg-green-100 dark:bg-green-900/50 text-green-700 dark:text-green-300"
                          : "bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400"
                      }`}
                    >
                      {site.available ? "Available" : "Booked"}
                    </span>
                  </div>
                  
                  <div className="space-y-1">
                    <div className="flex items-baseline gap-1">
                      <p className="text-3xl font-bold text-[#32B1FF]">
                        {site.price}
                      </p>
                    </div>
                    
                    {getTotalNights() > 0 && site.available && (
                      <div className="pt-2 border-t dark:border-gray-700">
                        <p className="text-xs text-gray-600 dark:text-gray-400">
                          Total for {getTotalNights()} night{getTotalNights() !== 1 ? 's' : ''}:
                        </p>
                        <p className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {calculateTotalPrice(site.price)}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
          </div>
        </div>
      </div>

      <SubscribeUpsellModal
        open={subscribeOpen}
        product={subscribeProduct}
        clientId={(localStorage.getItem("client_id") || "").toString()}
        onClose={() => setSubscribeOpen(false)}
        onStart={handleStartSubscription}
      />
    </div>
  );
}

export default PriceComparisonPage;

